import { identifyApiErrorCode } from '@/utils/axios';
import {
    CircleCircle,
    CircleMergedConfig,
    CustomAxiosError,
} from '@calm/evexia-web-sdk';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    skipToken,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { AxiosError } from 'axios';
import { useActiveCircle } from '../use-active-circle';

export const DEMO_PARTNER_INVALID_ERROR_CODE = 'demo_partner_invalid';
export const PARTNER_INVALID_ERROR_CODE = 'partner_invalid';

export enum InvalidSponsorshipReason {
    NotFound = 'NotFound',
    LostDemoSponsorship = 'LostDemoSponsorship',
    LostSponsorship = 'LostSponsorship',
    Error = 'Error',
}

interface SponsorshipQueryFnResponse {
    data: CircleMergedConfig | null;
    invalidSponsorshipReason: InvalidSponsorshipReason | null;
}

function identifyInvalidSponsorshipReason(
    error: AxiosError | CustomAxiosError | null
) {
    if (error?.response?.status === 404)
        return InvalidSponsorshipReason.NotFound;

    if (
        error?.response?.status === 403 &&
        identifyApiErrorCode(error) === PARTNER_INVALID_ERROR_CODE
    )
        return InvalidSponsorshipReason.LostSponsorship;

    if (
        error?.response?.status === 403 &&
        identifyApiErrorCode(error) === DEMO_PARTNER_INVALID_ERROR_CODE
    )
        return InvalidSponsorshipReason.LostDemoSponsorship;

    return InvalidSponsorshipReason.Error;
}

const mostRecentSponsorshipQueryOptions = {
    queryKey: (recipientId?: number) => {
        return [queryKeys.mostRecentSponsorship, recipientId];
    },
    queryFn: async ({
        recipientId,
        circleAuthorizationToken,
        getAccessTokenSilently,
    }: {
        recipientId: number;
        circleAuthorizationToken: string;
        getAccessTokenSilently: () => Promise<string>;
    }): Promise<SponsorshipQueryFnResponse> => {
        const circleApi = createCircleApi(getAccessTokenSilently);

        const { data, invalidSponsorshipReason } = await circleApi
            .getMostRecentSponsorship({
                recipientId,
                circleAuthorizationToken,
            })
            .then((response) => ({
                ...response,
                invalidSponsorshipReason: null,
            }))
            .catch((e: CustomAxiosError) => {
                const invalidSponsorshipReason =
                    identifyInvalidSponsorshipReason(e);

                if (invalidSponsorshipReason === InvalidSponsorshipReason.Error)
                    throw e;

                return { data: null, invalidSponsorshipReason };
            });
        return { data, invalidSponsorshipReason };
    },
    queryOptions: ({
        recipientId,
        isAuthenticated,
        isSponsored,
        circleAuthorizationToken,
    }: {
        recipientId?: number;
        isAuthenticated: boolean;
        isSponsored: boolean;
        circleAuthorizationToken: string | undefined;
    }) => {
        return {
            retry: false,
            enabled: Boolean(
                isAuthenticated &&
                    !!recipientId &&
                    isSponsored &&
                    circleAuthorizationToken
            ),
        };
    },
};

export function useMostRecentSponsorship() {
    const { activeCircle } = useActiveCircle();
    const recipientId = activeCircle?.recipient.id;
    const circleAuthorizationToken = activeCircle?.authorizationJWT;
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const isSponsored = Boolean(activeCircle?.sponsor);
    const { queryKey, queryFn, queryOptions } =
        mostRecentSponsorshipQueryOptions;
    const options = queryOptions({
        recipientId,
        isAuthenticated,
        isSponsored,
        circleAuthorizationToken,
    });

    const { data, error, ...otherProps } = useQuery<
        SponsorshipQueryFnResponse | null | undefined,
        CustomAxiosError
    >({
        queryKey: queryKey(recipientId),
        queryFn: circleAuthorizationToken
            ? async () =>
                  queryFn({
                      recipientId: recipientId!,
                      getAccessTokenSilently,
                      circleAuthorizationToken,
                  })
            : skipToken,
        ...options,
    });

    const { data: mostRecentSponsorship, invalidSponsorshipReason } =
        data || {};

    const hasLostSponsorship =
        activeCircle?.hasLostSponsorship ||
        invalidSponsorshipReason === InvalidSponsorshipReason.LostSponsorship ||
        invalidSponsorshipReason ===
            InvalidSponsorshipReason.LostDemoSponsorship;

    const isDemoSponsor = Boolean(
        mostRecentSponsorship?.isDemoSponsor ||
            invalidSponsorshipReason ===
                InvalidSponsorshipReason.LostDemoSponsorship
    );

    return {
        hasLostSponsorship,
        isDemoSponsor,
        mostRecentSponsorship,
        isEnabled: options.enabled,
        error,
        ...otherProps,
    };
}
export function useGetMostRecentSponsorship() {
    const { queryKey, queryFn } = mostRecentSponsorshipQueryOptions;
    const { getAccessTokenSilently } = useAuth();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (circle: CircleCircle) => {
            if (circle?.authorizationJWT && circle.recipient) {
                const response = await queryFn({
                    recipientId: circle.recipient.id,
                    getAccessTokenSilently,
                    circleAuthorizationToken: circle.authorizationJWT,
                });
                queryClient.setQueryData<SponsorshipQueryFnResponse | null>(
                    queryKey(circle.recipient.id),
                    response
                );
                return response.data;
            }
            return null;
        },
    });

    return {
        getMostRecentSponsorship: mutate,
        getMostRecentSponsorshipAsync: mutateAsync,
        ...otherProps,
    };
}
